import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {LinkIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Link, Textarea} from '@primer/react'
import {clsx} from 'clsx'
import {type ChangeEvent, type KeyboardEvent, useCallback, useMemo, useRef, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {commitUpdateProjectItemFieldValueMutation} from '../../../mutations/update-project-item-field-value'
import {containsSafeContent, isValidAndSafeUrl, prefixUrl, sanitizeInput} from '../../../utils/url'
import textStyles from '../../fields/IssueFieldTextToken.module.css'
import type {TextFieldConfigFragment$key} from './__generated__/TextFieldConfigFragment.graphql'
import type {TextFieldFragment$key} from './__generated__/TextFieldFragment.graphql'
import {FieldWrapper} from './FieldWrapper'
import type {BaseFieldProps} from './Shared'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const TextFieldFragment = graphql`
  fragment TextFieldFragment on ProjectV2ItemFieldTextValue {
    id
    text
    field {
      ...TextFieldConfigFragment @dangerously_unaliased_fixme
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const TextFieldConfigFragment = graphql`
  fragment TextFieldConfigFragment on ProjectV2Field {
    id
    name
  }
`

type TextFieldProps = BaseFieldProps<TextFieldConfigFragment$key, TextFieldFragment$key>

export function TextField({viewerCanUpdate, itemId, projectId, field, value, onIssueUpdate}: TextFieldProps) {
  const environment = useRelayEnvironment()

  const inputRef = useRef<HTMLTextAreaElement>(null)

  const fieldData = useFragment(TextFieldConfigFragment, field)
  const valueData = useFragment(TextFieldFragment, value)

  const fieldId = fieldData.id
  const fieldName = fieldData.name

  const [showInput, setShowInput] = useState(false)

  const originalValue = valueData?.text ?? ''

  const [currentValue, setCurrentValue] = useState(originalValue)

  // Text editor is clean while the user hasn't made any changes,
  // prevent navigating away if there are unsaved changes
  const [isClean, setIsClean] = useState<boolean>(true)

  const [shouldRestoreRowFocus, setShouldRestoreRowFocus] = useState(false)

  const commitCurrentValue = useCallback(() => {
    commitUpdateProjectItemFieldValueMutation({
      environment,
      input: {
        fieldId,
        itemId,
        projectId,
        value: {text: currentValue},
      },
      onCompleted: onIssueUpdate,
    })
  }, [environment, fieldId, itemId, projectId, currentValue, onIssueUpdate])

  const onChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value
      setCurrentValue(newValue)
      if (newValue === originalValue) {
        setIsClean(true)
      } else {
        setIsClean(false)
      }
    },
    [originalValue],
  )

  const onUpdate = useCallback(() => {
    commitCurrentValue()
    setShowInput(false)
    setIsClean(true)
    setShouldRestoreRowFocus(true)
  }, [commitCurrentValue])

  const onCancel = useCallback(() => {
    if (inputRef.current) {
      setCurrentValue(valueData?.text ?? '')
      // eslint-disable-next-line github/no-blur
      inputRef.current.blur()
      setShowInput(false)
      setIsClean(true)
      setShouldRestoreRowFocus(true)
    }
  }, [valueData?.text])

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter') {
        // We need to stop this as the key events sometimes are handled outside React’s normal event pipeline,
        // so the component doesn’t re-render in time, and we cannot restore the focus to the button. This doesn't
        // have any negative side effects as we handle form submission carefully.
        e.preventDefault()
        onUpdate()
      }
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCancel()
      }
    },
    [onUpdate, onCancel],
  )

  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const anchorRef = useRef<HTMLLIElement | null>(null)

  // eslint-disable-next-line @eslint-react/no-unnecessary-use-callback
  const linkHandler = useCallback(
    (link: string) => () => ssrSafeWindow?.open(prefixUrl(link), '_blank', 'noreferrer'),
    [],
  )
  const editHandler = useCallback(() => setShowInput(true), [setShowInput])
  const clickHandler = useCallback(() => setMenuOpen(true), [setMenuOpen])
  const linkOnKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter') {
        e.preventDefault()
        clickHandler()
      }
    },
    [clickHandler],
  )
  const linkOnClick = useCallback(
    // If the viewer can't edit we allow the default link behavior
    (e: React.MouseEvent<HTMLAnchorElement>) => viewerCanUpdate && e.preventDefault(),
    [viewerCanUpdate],
  )
  // Break the text value into fragments to build the html and actions
  const fragments = useMemo(() => currentValue.split(/(\s+)/), [currentValue])
  const [currentValueHtml, containsUrl, urls] = useMemo(() => {
    const urlList: string[] = []
    let contains = false
    const html = fragments.map((fragment, key) => {
      const sanitizedFragment = sanitizeInput(fragment)
      const prefixedUrl = prefixUrl(sanitizedFragment)
      if (isValidAndSafeUrl(prefixedUrl) && containsSafeContent(prefixedUrl)) {
        // eslint-disable-next-line react-hooks/immutability
        contains = true
        urlList.push(fragment)

        return (
          <Link
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`text-field-link-${key}`}
            tabIndex={0}
            href={prefixUrl(fragment)}
            onKeyDown={linkOnKeyDown}
            onClick={linkOnClick}
            target="_blank"
          >
            {fragment}
          </Link>
        )
      } else {
        return fragment
      }
    })

    return [html, contains, urlList]
  }, [fragments, linkOnClick, linkOnKeyDown])

  const anchorProps = containsUrl ? {onClick: clickHandler} : undefined
  const actionMenu = (
    <ActionMenu open={menuOpen} onOpenChange={open => setMenuOpen(open)} anchorRef={anchorRef}>
      <ActionMenu.Overlay width="small">
        <ActionList>
          <ActionList.Group>
            {urls?.map((url, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <ActionList.Item key={index} onSelect={linkHandler(url)} role={'button'}>
                <ActionList.LeadingVisual>
                  <LinkIcon />
                </ActionList.LeadingVisual>
                {url}
              </ActionList.Item>
            ))}
            {viewerCanUpdate && (
              <ActionList.Item onSelect={editHandler} role={'button'}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
            )}
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        showInput={showInput}
        setShowInput={setShowInput}
        placeholder={`No ${fieldName}`}
        canUpdate={false}
        value={currentValue ? currentValueHtml : undefined}
        name={fieldName}
      />
    )

  return (
    <>
      <FieldWrapper
        ref={anchorRef}
        value={currentValue ? currentValueHtml : undefined}
        anchorProps={anchorProps}
        name={fieldName}
        placeholder="Enter text…"
        showInput={showInput}
        setShowInput={setShowInput}
        onCommit={commitCurrentValue}
        isClean={isClean}
        setIsClean={setIsClean}
        shouldRestoreRowFocus={shouldRestoreRowFocus}
        setShouldRestoreRowFocus={setShouldRestoreRowFocus}
        input={
          <Textarea
            data-testid="text-field-input"
            placeholder="Enter text…"
            ref={inputRef}
            resize="none"
            onChange={onChange}
            onKeyDown={onKeyDown}
            value={currentValue}
            className={clsx(textStyles.issueFieldTextInput, textStyles.issueFieldTextTextarea)}
            onFocus={event => event.target.setSelectionRange(event.target.value.length, event.target.value.length)}
            autoFocus
          />
        }
      />
      {actionMenu}
    </>
  )
}
